import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError, api } from './api';
import { t } from './i18n';

export interface PlannerSettings {
  enabled: boolean;
  transfer_max_m: number;
  min_transfer_sec: number;
  max_transfers: number;
  access_max_m: number;
  walk_only_always_m: number;
  walk_only_max_m: number;
  walk_speed_kmh: number;
  walk_reluctance: number;
  log_requests: boolean;
}

export interface PlannerStatus {
  up: boolean;
  error?: string;
  started_at?: string;
  loaded_at?: string | null;
  load_ms?: number;
  next_load_at?: string | null;
  settings_read_at?: string | null;
  settings?: PlannerSettings;
  network?: {
    stations: number;
    lines: number;
    directions: number;
    planned: { week: number; weekend: number };
    missing: Array<{ id: string; sign: string; headsign: string; week: boolean; weekend: boolean }>;
    segments: { measured: number; total: number };
    unserved: Array<{ id: string; name: string }>;
  } | null;
  trips?: { wd: number; sat: number; sun: number } | null;
  walk?: {
    nodes: number;
    edges: number;
    built_at: string | null;
    build_ms: number;
    off_network: Array<{ id: string; name: string; m: number | null }>;
  } | null;
  transfers?: { pairs: number; streets: boolean; ms: number } | null;
  places?: { indexed: number; osm_built_at: string | null };
  requests?: {
    count: number;
    empty: number;
    p50_ms: number | null;
    p95_ms: number | null;
    max_ms: number | null;
  };
  memory_mb?: { rss: number; heap: number };
}

type Counts = Record<
  | 'n'
  | 'empty'
  | 'walk'
  | 'arrive_by'
  | 'later'
  | 'direct'
  | 'one_change'
  | 'more_changes'
  | 'live'
  | 'failed'
  | 'ms'
  | 'minutes'
  | 'searches'
  | 'searches_empty',
  number
>;
export interface Area {
  lat: number;
  lng: number;
  name?: string | null;
}
export interface PlannerStats {
  days: number;
  totals: Counts;
  by_day: Array<Counts & { d: string }>;
  by_hour: Array<{ h: number; n: number; searches: number }>;
  origins: Array<Area & { n: number; empty: number }>;
  destinations: Array<Area & { n: number; empty: number }>;
  top_pairs: Array<{ from: Area; to: Area; n: number; empty: number }>;
  no_route: Array<{ from: Area; to: Area; n: number; empty: number }>;
  misses: Array<{ q: string; n: number }>;
}
export interface PlanLogItem {
  t: string;
  from: [number, number];
  to: [number, number];
  arrive_by: boolean;
  at: string | null;
  n: number;
  minutes: number | null;
  transfers: number | null;
  walk_m: number | null;
  walk_only: boolean;
  lines: string;
  ms: number;
  error: boolean;
}
export interface PlannerStorage {
  collections: Array<{ name: string; count: number; bytes: number | null }>;
  osm: { builtAt?: number; count?: number; source?: string } | null;
}

/** A planned trip as the planner answers (the parts the admin shows). */
export interface TryLeg {
  mode: 'walk' | 'bus';
  sign?: string;
  color?: string;
  headsign?: string;
  depart: string;
  arrive: string;
  distance_m?: number;
  stops?: number;
  shape?: string;
  from: { lat: number; lng: number };
  to: { lat: number; lng: number };
}
export interface TryItinerary {
  depart: string;
  arrive: string;
  minutes: number;
  walk_m: number;
  transfers: number;
  legs: TryLeg[];
}

export const usePlannerSettings = () =>
  useQuery({
    queryKey: ['planner', 'settings'],
    queryFn: () =>
      api<{ settings: PlannerSettings & { updated_at: string | null }; defaults: PlannerSettings }>(
        '/settings/planner',
      ),
  });

export function useSavePlannerSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (s: PlannerSettings) => api('/settings/planner', { method: 'PUT', body: s }),
    onSuccess: (data) => {
      qc.setQueryData(['planner', 'settings'], data);
      toast.success(t('Saved. The planner applies it within a minute.'));
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('Could not save')),
  });
}

export const usePlannerStatus = () =>
  useQuery({
    queryKey: ['planner', 'status'],
    queryFn: () => api<PlannerStatus>('/planner/status'),
    refetchInterval: 30_000,
  });

export const usePlannerStats = (days: number) =>
  useQuery({
    queryKey: ['planner', 'stats', days],
    queryFn: () => api<PlannerStats>('/planner/stats', { query: { days } }),
  });

export const usePlannerRecent = (empty: boolean) =>
  useQuery({
    queryKey: ['planner', 'recent', empty],
    queryFn: () =>
      api<{ items: PlanLogItem[] }>('/planner/recent', { query: { limit: 50, empty: String(empty) } }),
    refetchInterval: 60_000,
  });

export const usePlannerStorage = () =>
  useQuery({ queryKey: ['planner', 'storage'], queryFn: () => api<PlannerStorage>('/planner/storage') });

/** Decodes an encoded polyline (precision 5) to [lng, lat] pairs. */
export function decodePolyline(s: string): [number, number][] {
  const out: [number, number][] = [];
  let i = 0;
  let lat = 0;
  let lng = 0;
  const next = () => {
    let shift = 0;
    let result = 0;
    let b: number;
    do {
      b = s.charCodeAt(i++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    return result & 1 ? ~(result >> 1) : result >> 1;
  };
  while (i < s.length) {
    lat += next();
    lng += next();
    out.push([lng / 1e5, lat / 1e5]);
  }
  return out;
}
